/**
 * @module features/entity/entity-loader
 * @description Entity file loading orchestration for all entity types
 */

import { logger } from '../../utils/logger.js';
import { ENTITY_TYPES } from './entity-types.js';
import { getEntityMetadata, getEntityFiles } from '../../core/api/entity-api.js';
import { displayEntityMetadata } from './entity-metadata.js';
import { displayFiles, highlightFile } from '../files/file-list.js';
import { updateBreadcrumb } from '../navigation/breadcrumb.js';
import { getDOMElements } from '../../ui/dom/dom-manager.js';
import { showErrorWithRecovery, detectErrorType } from '../../ui/components/error-recovery.js';
import { showLoading, hideLoading, LOADING_STATES } from '../../ui/components/loading-indicator.js';
import { addRecentItem } from '../navigation/recent-items.js';

/**
 * Load entity files and metadata
 * Main orchestration function for loading entity data
 *
 * @param {Object} user - Firebase user object
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts)
 * @param {string} entityId - ID of the entity
 * @param {string|null} fileId - Optional file ID to highlight after loading
 * @throws {Error} If entity type is unknown or API calls fail
 */
export async function loadEntityFiles(user, entityType, entityId, fileId = null) {
    const config = ENTITY_TYPES[entityType];
    if (!config) {
        throw new Error(`Unknown entity type: ${entityType}`);
    }

    // Validate entityId is provided
    if (!entityId || entityId.trim() === '') {
        throw new Error(`Invalid entity ID provided for ${entityType}`);
    }

    const dom = getDOMElements;

    showLoading(LOADING_STATES.INITIALIZING, `Loading ${config.label.toLowerCase()}...`);
    dom.filesSection.classList.add('hidden');
    dom.errorMessage.classList.add('hidden');

    try {
        // Step 1: Load metadata
        showLoading(LOADING_STATES.LOADING_METADATA, `Loading ${config.label.toLowerCase()} metadata...`);
        const metadata = await getEntityMetadata(entityType, entityId);

        // Step 2: Load files
        showLoading(LOADING_STATES.LOADING_FILES, `Loading ${config.label.toLowerCase()} files...`);
        const files = await getEntityFiles(entityType, entityId);

        // Get entity name for breadcrumb from the API response
        const entityName = metadata.entityName || 'Untitled';

        // Display metadata and files with entity awareness
        displayEntityMetadata(metadata, entityType, config);
        await displayFiles(files, user, fileId, entityType, entityId);

        // Update breadcrumb navigation
        updateBreadcrumb(entityType, entityName, entityId);

        // Add to recent items
        addRecentItem(entityType, entityId, entityName);

        hideLoading();
        dom.filesSection.classList.remove('hidden');

        // If a specific file ID was provided, highlight and scroll to it
        if (fileId) {
            logger.debug(`📌 File ID from URL: ${fileId}`);
            highlightFile(fileId);
        }
    } catch (error) {
        logger.error(`Error loading ${config.label.toLowerCase()} files:`, error);
        hideLoading();
        dom.filesSection.classList.remove('hidden');

        // Extract status code if available
        let statusCode;
        if (error.message.includes('404')) {
            statusCode = 404;
        } else if (error.message.includes('401')) {
            statusCode = 401;
        } else if (error.message.includes('403')) {
            statusCode = 403;
        } else if (error.message.match(/\d{3}/)) {
            statusCode = parseInt(error.message.match(/\d{3}/)[0]);
        }

        // Detect error type
        const errorType = detectErrorType(error, statusCode);

        // Show enhanced error with recovery options
        showErrorWithRecovery(error, {
            type: errorType,
            entityType,
            entityId,
            entityName: config.label,
            statusCode,
            retryAction: () => loadEntityFiles(user, entityType, entityId, fileId)
        });
    }
}

/**
 * Load entity metadata only (without files)
 * @deprecated Use getEntityMetadata from entity-api.js instead
 * @param {string} entityType - Type of entity
 * @param {string} entityId - ID of the entity
 * @returns {Promise<Object>} Entity metadata
 */
export async function loadEntityMetadata(entityType, entityId) {
    // Delegate to the API client function
    return await getEntityMetadata(entityType, entityId);
}

/**
 * Load entity files list only (without metadata)
 * @deprecated Use getEntityFiles from entity-api.js instead
 * @param {string} entityType - Type of entity
 * @param {string} entityId - ID of the entity
 * @returns {Promise<Array>} Array of file objects
 */
export async function loadEntityFilesList(entityType, entityId) {
    // Delegate to the API client function
    return await getEntityFiles(entityType, entityId);
}

/**
 * Reload current entity (refresh files and metadata)
 * @param {Object} user - Firebase user object
 * @param {string} entityType - Type of entity
 * @param {string} entityId - ID of the entity
 */
export async function reloadEntity(user, entityType, entityId) {
    await loadEntityFiles(user, entityType, entityId);
}

export default {
    loadEntityFiles,
    loadEntityMetadata,
    loadEntityFilesList,
    reloadEntity
};
